// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useSubmitting } from '@tectonics/prs/submitting';
import { InputGroup } from '../../../../../../commons/molecules';
import { Label, TextField } from '../../../../../../commons/atoms';
import { ErrorLabel, InputHolder } from '../../commons';
import type { Country } from '@tectonics/prs/country';

export function InputCardHolderName(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const [value, handleChange] = useUserInputAsTextField('cardHolderName');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardHolderName');
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  return (
    <InputHolder data-test-name="card-holder-name">
      <InputGroup
        label={
          <Label htmlFor={inputId}>
            <LabelMessage />
          </Label>}
        input={
          <TextField
            id={inputId}
            value={value}
            onChange={handleChange}
            fashion={formHasBeenSent && validationError ? 'error' : null}
            readOnly={submitting}
            data-error={formHasBeenSent && validationError ? 'error' : null}
            data-secret
            name="card-holder-name"
          />
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage country={country} validationError={validationError} />
      )}
    </InputHolder>
  );
}

function LabelMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Nome (como impresso no cartão)';
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'cr':
    case 'gt':
    case 'sv':
    case 'pa':
    case 'do':
      return 'Nombre del titular de la tarjeta';
    case 'za':
    case 'ke':
    case 'ng':
    case 'tz':
    case 'in':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
    case 'jm':
      return 'Full name (as shown in card)';
    default:
      throw new Error(`No message found for field card holder name in country ${country}`);
  }
}

type ValidationErrorMessageProps = {|
  country: Country,
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return {
        br: 'Faltou uma informação aqui! Preencha com o nome que aparece em seu cartão.',
        mx: 'Completa tu nombre como aparece en tu tarjeta',
        ar: 'Completa tu nombre como aparece en tu tarjeta',
        co: 'Completa tu nombre como aparece en tu tarjeta',
        cl: 'Completa tu nombre como aparece en tu tarjeta',
        pe: 'Completa tu nombre como aparece en tu tarjeta',
        ec: 'Completa tu nombre como aparece en tu tarjeta',
        bo: 'Completa tu nombre como aparece en tu tarjeta',
        uy: 'Completa tu nombre como aparece en tu tarjeta',
        py: 'Completa tu nombre como aparece en tu tarjeta',
        cr: 'Completa tu nombre como aparece en tu tarjeta',
        pa: 'Completa tu nombre como aparece en tu tarjeta',
        gt: 'Completa tu nombre como aparece en tu tarjeta',
        sv: 'Completa tu nombre como aparece en tu tarjeta',
        do: 'Completa tu nombre como aparece en tu tarjeta',
        za: 'Complete with the name as it appears on the card',
        ke: 'Complete with the name as it appears on the card',
        ng: 'Complete with the name as it appears on the card',
        tz: 'Complete with the name as it appears on the card',
        in: 'Complete with the name as it appears on the card',
        ug: 'Complete with the name as it appears on the card',
        zm: 'Complete with the name as it appears on the card',
        eg: 'Complete with the name as it appears on the card',
        gh: 'Complete with the name as it appears on the card',
        jm: 'Complete with the name as it appears on the card',
      };

    case 'contains-number':
      return {
        br: 'Nome não pode conter números',
        mx: 'El nombre no puede contener números',
        ar: 'El nombre no puede contener números',
        co: 'El nombre no puede contener números',
        cl: 'El nombre no puede contener números',
        pe: 'El nombre no puede contener números',
        ec: 'El nombre no puede contener números',
        bo: 'El nombre no puede contener números',
        uy: 'El nombre no puede contener números',
        py: 'El nombre no puede contener números',
        cr: 'El nombre no puede contener números',
        gt: 'El nombre no puede contener números',
        sv: 'El nombre no puede contener números',
        do: 'El nombre no puede contener números',
        pa: 'El nombre no puede contener números',
        za: 'The name cannot contain numbers',
        ke: 'The name cannot contain numbers',
        ng: 'The name cannot contain numbers',
        tz: 'The name cannot contain numbers',
        in: 'The name cannot contain numbers',
        ug: 'The name cannot contain numbers',
        zm: 'The name cannot contain numbers',
        eg: 'The name cannot contain numbers',
        gh: 'The name cannot contain numbers',
        jm: 'The name cannot contain numbers',
      };

    case 'max-length':
      return {
        br: 'Nome não pode conter mais de 50 caracteres',
        mx: 'El nombre no puede contener más de 50 caracteres',
        ar: 'El nombre no puede contener más de 50 caracteres',
        co: 'El nombre no puede contener más de 50 caracteres',
        cl: 'El nombre no puede contener más de 50 caracteres',
        pe: 'El nombre no puede contener más de 50 caracteres',
        ec: 'El nombre no puede contener más de 50 caracteres',
        bo: 'El nombre no puede contener más de 50 caracteres',
        uy: 'El nombre no puede contener más de 50 caracteres',
        py: 'El nombre no puede contener más de 50 caracteres',
        cr: 'El nombre no puede contener más de 50 caracteres',
        do: 'El nombre no puede contener más de 50 caracteres',
        sv: 'El nombre no puede contener más de 50 caracteres',
        gt: 'El nombre no puede contener más de 50 caracteres',
        pa: 'El nombre no puede contener más de 50 caracteres',
        za: 'Name cannot contain more than 50 characters',
        ke: 'Name cannot contain more than 50 characters',
        ng: 'Name cannot contain more than 50 characters',
        tz: 'Name cannot contain more than 50 characters',
        in: 'Name cannot contain more than 50 characters',
        ug: 'Name cannot contain more than 50 characters',
        zm: 'Name cannot contain more than 50 characters',
        eg: 'Name cannot contain more than 50 characters',
        gh: 'Name cannot contain more than 50 characters',
        jm: 'Name cannot contain more than 50 characters',
      };

    default:
      return {
        br: 'Nome inválido',
        mx: 'nombre inválido',
        ar: 'nombre inválido',
        co: 'nombre inválido',
        cl: 'nombre inválido',
        pe: 'nombre inválido',
        ec: 'nombre inválido',
        bo: 'nombre inválido',
        uy: 'nombre inválido',
        py: 'nombre inválido',
        cr: 'nombre inválido',
        do: 'nombre inválido',
        pa: 'nombre inválido',
        sv: 'nombre inválido',
        gt: 'nombre inválido',
        za: 'Invalid name',
        ke: 'Invalid name',
        ng: 'Invalid name',
        tz: 'Invalid name',
        in: 'Invalid name',
        ug: 'Invalid name',
        zm: 'Invalid name',
        eg: 'Invalid name',
        gh: 'Invalid name',
        jm: 'Invalid name',
      };
  }
};

function ValidationErrorMessage({ country, validationError }: ValidationErrorMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError)[country] || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}
